<script setup lang="ts">
import { ref, watch, computed, onMounted } from 'vue'
import { useMetricOptions } from '../composables/use-metric-options'
import { useInsightsStore } from '../stores/insights-store'
import VSelect from '@/modules/investing/components/v-select.vue'

const emit = defineEmits<{
  (e: 'apply', params: Record<string, any>): void
}>()

const insightsStore = useInsightsStore()

const selectedMetric = ref<string | null>(null)
const selectedScope = ref<string | null>('global')
const selectedYear = ref<number | null>(null)
const selectedYears = ref<string[]>([])
const selectedGroupId = ref<string | null>(null)
const selectedInvestorSetId = ref<string | null>(null)
const debugEnabled = ref(false)

const { categories, availableScopes, supportsYear, supportsMultiYear, yearOptions, buildParams } =
  useMetricOptions(selectedMetric)

const multiYearOptions = computed(() => yearOptions.value.map((y) => ({ label: String(y), value: String(y) })))

const needsGroup = computed(() => selectedScope.value === 'group' || selectedScope.value === 'investor_set')
const needsInvestorSet = computed(() => selectedScope.value === 'investor_set')

const groupOptions = computed(() => insightsStore.groups.map((g) => ({ label: g.name, value: String(g.id) })))

const investorSetOptions = computed(() =>
  insightsStore.investorSets.map((is) => ({
    label: `${is.name} (${is.entity_type || 'unknown'})`,
    value: String(is.id),
  })),
)

const groupIdAsNumber = computed(() => (selectedGroupId.value ? parseInt(selectedGroupId.value, 10) : null))

const investorSetIdAsNumber = computed(() =>
  selectedInvestorSetId.value ? parseInt(selectedInvestorSetId.value, 10) : null,
)

onMounted(() => {
  insightsStore.fetchGroups()
})

// When group changes, fetch its investor sets
watch(selectedGroupId, (groupId) => {
  selectedInvestorSetId.value = null
  if (groupId && needsInvestorSet.value) {
    insightsStore.fetchInvestorSets(parseInt(groupId, 10))
  }
})

// When scope changes to investor_set and group is already selected, fetch investor sets
watch(selectedScope, (scope) => {
  selectedInvestorSetId.value = null
  if (scope !== 'group' && scope !== 'investor_set') {
    selectedGroupId.value = null
  }
  if (scope === 'investor_set' && groupIdAsNumber.value) {
    insightsStore.fetchInvestorSets(groupIdAsNumber.value)
  }
})

watch(
  [selectedMetric, selectedScope, selectedYear, selectedYears, selectedGroupId, selectedInvestorSetId, debugEnabled],
  () => {
    if (!selectedMetric.value) return

    // Reset scope if current selection is no longer available
    if (availableScopes.value.length && !availableScopes.value.find((s) => s.value === selectedScope.value)) {
      selectedScope.value = availableScopes.value[0]?.value || 'global'
    }

    // Don't fire until all required fields are filled
    if (needsGroup.value && !selectedGroupId.value) return
    if (needsInvestorSet.value && !selectedInvestorSetId.value) return
    if (supportsMultiYear.value && !selectedYears.value.length) return

    const yearsAsNumbers = supportsMultiYear.value ? selectedYears.value.map((y) => parseInt(y, 10)) : null

    const params = buildParams(
      selectedMetric.value,
      selectedScope.value,
      supportsYear.value ? selectedYear.value : null,
      groupIdAsNumber.value,
      investorSetIdAsNumber.value,
      yearsAsNumbers,
    )
    if (debugEnabled.value) params.debug = true
    emit('apply', params)
  },
)
</script>

<template>
  <div class="flex flex-wrap items-end gap-4">
    <!-- Metric -->
    <div class="min-w-[240px] flex-1">
      <label class="mb-1 block text-sm font-medium text-gray-700">Metric</label>
      <select
        v-model="selectedMetric"
        class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
      >
        <option :value="null" disabled>Select a metric...</option>
        <optgroup v-for="(options, category) in categories" :key="category" :label="category">
          <option v-for="option in options" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </optgroup>
      </select>
    </div>

    <!-- Scope -->
    <div v-if="availableScopes.length > 1" class="min-w-[180px]">
      <label class="mb-1 block text-sm font-medium text-gray-700">Scope</label>
      <select
        v-model="selectedScope"
        class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
      >
        <option v-for="scope in availableScopes" :key="scope.value" :value="scope.value">
          {{ scope.label }}
        </option>
      </select>
    </div>

    <!-- Group Select (shown for both group and investor_set scopes) -->
    <div v-if="needsGroup" class="min-w-[240px]">
      <VSelect
        v-model="selectedGroupId"
        :options="groupOptions"
        label="Group"
        placeholder="Select a group..."
        size="sm"
      />
    </div>

    <!-- Investor Set Select (shown after group is selected) -->
    <div v-if="needsInvestorSet && selectedGroupId" class="min-w-[240px]">
      <VSelect
        v-model="selectedInvestorSetId"
        :options="investorSetOptions"
        label="Investor Set"
        :placeholder="investorSetOptions.length ? 'Select an investor set...' : 'Loading...'"
        size="sm"
      />
    </div>

    <!-- Year -->
    <div v-if="supportsYear" class="min-w-[120px]">
      <label class="mb-1 block text-sm font-medium text-gray-700">Year</label>
      <select
        v-model="selectedYear"
        class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
      >
        <option :value="null">Default</option>
        <option v-for="year in yearOptions" :key="year" :value="year">
          {{ year }}
        </option>
      </select>
    </div>

    <!-- Years (multi-select) -->
    <div v-if="supportsMultiYear" class="min-w-[240px]">
      <VSelect
        v-model="selectedYears"
        :options="multiYearOptions"
        :multi="true"
        label="Years"
        placeholder="Select one or more years..."
        size="sm"
      />
    </div>

    <!-- Debug Toggle -->
    <div class="flex items-end pb-1">
      <label class="inline-flex cursor-pointer items-center gap-2">
        <input
          v-model="debugEnabled"
          type="checkbox"
          class="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        />
        <span class="text-sm font-medium text-gray-700">Debug</span>
      </label>
    </div>
  </div>
</template>
